/**
 * Pool Spec resource pickers — GPU and CPU shape pull-downs that
 * replace the free-text ``accelerators`` / ``cpus`` inputs.
 *
 * The previous free-text inputs let operators commit typos like
 * "RTX A6000" (with space) or counts no provider could serve and only
 * surfaced the failure at provisioning time. The pickers source their
 * options from the controller's ``/pools/:id/resource-catalog``
 * endpoint, which unions every shape SkyPilot's local catalog knows
 * about across the compute clouds we model and annotates each option
 * with which providers can serve it.
 *
 * Visual language:
 *
 * * Each row in the picker lists the shape on the left and a strip
 *   of provider chips on the right.
 * * **Enabled chips** (the operator has at least one variant of that
 *   cloud turned on in this pool's inheritance chain) are rendered
 *   solid in the brand colour — picking this row means SkyPilot will
 *   actually consider that cloud.
 * * **Disabled chips** (catalog-available but the provider is off)
 *   are rendered as dashed outlines with a "not enabled" tooltip —
 *   visible so the operator can discover "enable AWS to get H100:8"
 *   without bouncing through the Providers tab first, but visually
 *   clearly distinct from active options.
 * * If a row has *no* enabled providers at all, the row itself is
 *   rendered with a warning border so the operator can't miss that
 *   picking it would fail to provision until they enable a matching
 *   provider.
 *
 * Both pickers keep an "Advanced — type a custom selector" toggle
 * that falls back to the original free-form input for the rare cases
 * the picker doesn't surface (TPU pods, GPU types added to SkyPilot
 * but not yet to the local catalog, etc.).
 */
import { useEffect, useMemo, useRef, useState } from "react";

import type {
  CpuShape,
  GpuOption,
  ResourceCatalog,
  ResourceProviderAvailability,
} from "@/api/types";
import { humanCloudLabel } from "@/workloads/tree-data";

/**
 * Parse the CSV ``accelerators`` value stored on a Pool into a list
 * of ``{name, count}`` entries.
 *
 * The controller persists ``accelerators`` as either a single
 * ``"H100:1"`` (legacy / single-shape case) or comma-separated
 * ``"A6000:1,RTXA6000:1"`` (multi-candidate case the SkyPilot
 * optimiser will fan out across). Malformed entries (missing colon,
 * non-numeric count) are silently dropped so the picker can recover
 * gracefully from pre-existing rows or hand-edited overrides; the
 * Pydantic validator on the controller is the real gate.
 *
 * Mirrors :func:`controller.skypilot.renderer.parse_accelerators_csv`
 * one-to-one so the round-trip UI <-> server is lossless.
 */
function parseAcceleratorList(
  value: string | undefined | null,
): Array<{ name: string; count: number }> {
  if (!value) return [];
  const trimmed = value.trim();
  if (trimmed === "") return [];
  const out: Array<{ name: string; count: number }> = [];
  for (const token of trimmed.split(",")) {
    const entry = token.trim();
    if (!entry || !entry.includes(":")) continue;
    const [namePart, countPart] = entry.split(":", 2);
    const name = namePart.trim();
    const count = Number(countPart);
    if (!name || !Number.isFinite(count) || count < 1) continue;
    out.push({ name, count });
  }
  return out;
}

/**
 * Stringify the inverse: turn the ordered selection into the CSV
 * shape the controller expects. Order is preserved (the picker
 * keeps the insertion order so the rendered YAML matches what the
 * operator selected from top to bottom, which is what shows up in
 * SkyPilot's optimiser cost table).
 */
function serializeAcceleratorList(
  entries: Array<{ name: string; count: number }>,
): string {
  return entries.map((e) => `${e.name}:${e.count}`).join(",");
}

interface ProviderChipsProps {
  providers: ResourceProviderAvailability[];
}

/**
 * Format one ``ResourceProviderPrice`` row as a tooltip line.
 *
 * The catalog may carry on-demand only, spot only, or both. We
 * collapse the missing half rather than rendering "$null/hr" — same
 * rule the SkyPilot optimiser uses when costing candidates.
 */
function formatPriceLine(p: {
  count: number;
  on_demand: number | null;
  spot: number | null;
}): string {
  const fmt = (v: number): string =>
    // 3 sig figs is enough to distinguish $0.0792 from $0.0794 while
    // staying readable for the "$14.32" common case.
    v < 0.01
      ? `$${v.toFixed(4)}`
      : v < 1
        ? `$${v.toFixed(3)}`
        : `$${v.toFixed(2)}`;
  const parts: string[] = [];
  if (p.on_demand != null) parts.push(`${fmt(p.on_demand)}/hr`);
  if (p.spot != null) parts.push(`spot ${fmt(p.spot)}/hr`);
  if (parts.length === 0) return `×${p.count}: (no price)`;
  return `×${p.count}: ${parts.join(" · ")}`;
}

/**
 * Build the multi-line ``title`` text for one provider chip.
 *
 * Standard browser tooltips don't render HTML but they do honour
 * ``\n`` line breaks, which is enough to stack "×1: $0.79/hr" rows
 * under the variant breakdown without pulling in a tooltip library.
 */
function buildProviderTooltip(p: ResourceProviderAvailability): string {
  const head = p.enabled
    ? "Enabled in this pool's provider chain"
    : "Available in the catalog but provider is not enabled — enable it on the Providers tab to use this shape";
  const lines: string[] = [head];
  if (p.variant_keys.length > 0) {
    lines.push(`variants: ${p.variant_keys.join(", ")}`);
  }
  if (p.prices && p.prices.length > 0) {
    // Header row separates the variant line from the price table so
    // the operator's eye lands on the price column directly when
    // they hover for cost comparison.
    lines.push("price:");
    for (const pr of p.prices) {
      lines.push(`  ${formatPriceLine(pr)}`);
    }
  }
  return lines.join("\n");
}

/**
 * Horizontal chip strip rendered on the right of every picker row.
 *
 * Sorts enabled providers first so the operator's eye lands on the
 * usable half of the row. ``title`` carries the variant breakdown +
 * per-count $/hr price table for hover discovery — fitting either of
 * those on the row would blow out horizontally on the RunPod (×2
 * variants) + AWS (×2) + GCP (×2) common case.
 */
function ProviderChips({ providers }: ProviderChipsProps) {
  if (providers.length === 0) {
    return (
      <span className="resource-picker__chip resource-picker__chip--none">
        no providers
      </span>
    );
  }
  const sorted = [...providers].sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return a.cloud.localeCompare(b.cloud);
  });
  return (
    <div className="resource-picker__chips">
      {sorted.map((p) => (
        <span
          key={p.cloud}
          className={
            "resource-picker__chip " +
            (p.enabled
              ? "resource-picker__chip--enabled"
              : "resource-picker__chip--disabled")
          }
          title={buildProviderTooltip(p)}
        >
          {humanCloudLabel(p.cloud)}
        </span>
      ))}
    </div>
  );
}

interface GpuPickerProps {
  /**
   * Current ``accelerators`` value. Single shape ``"RTXA6000:4"`` or
   * comma-separated multi-candidate list ``"A6000:1,RTXA6000:1"``.
   */
  value: string;
  onChange: (next: string) => void;
  catalog: ResourceCatalog | undefined;
  isLoading: boolean;
}

/**
 * Multi-select GPU picker rendered as an inline panel inside the
 * Pool Spec tab.
 *
 * Why a panel instead of a dropdown:
 *
 *   * SkyPilot's ``resources.accelerators`` accepts a list of
 *     candidate shapes and lets its optimiser pick the cheapest
 *     available one across the candidate clouds. Picking "A6000 or
 *     RTXA6000 or RTX_A6000" is the natural fix for the per-cloud
 *     naming drift we've been papering over (Lambda calls it
 *     ``A6000``, RunPod calls it ``RTXA6000``, PrimeIntellect calls
 *     it ``RTX_A6000``) — but you can't express "either of three"
 *     in a single ``<select>``.
 *   * Provider chip strips are the existing affordance for "where
 *     can this shape actually run"; surfacing them per GPU row in
 *     the panel makes the picker the discovery tool for "enable
 *     AWS to get H100" too, instead of a separate doc tour.
 *
 * Layout:
 *
 *   * Top toolbar: text filter + "enabled providers only" toggle +
 *     default count selector (applies to newly toggled rows; does
 *     not retroactively change rows that already have a count).
 *   * Scrollable list of GPU rows. Each row carries a checkbox, the
 *     GPU name, an optional per-row count override (only visible
 *     when the row is selected), and the provider chip strip.
 *     Rows whose enabled-provider list is empty get the warning
 *     border so the operator can't miss "this picks but nothing
 *     can serve it".
 *   * Footer with the selection summary and an "Advanced" escape
 *     hatch that drops back to the original free-form input for
 *     selectors the panel doesn't model (TPU pods, hand-edited CSV
 *     beyond the round-trip the panel produces, etc.).
 */
export function GpuPicker({
  value,
  onChange,
  catalog,
  isLoading,
}: GpuPickerProps) {
  const incoming = useMemo(() => parseAcceleratorList(value), [value]);

  // Track which GPUs in the incoming value aren't in the catalog so
  // we can keep them as "custom" rows at the top of the list (the
  // operator must be able to round-trip a value the picker doesn't
  // recognise without it silently disappearing on the first save).
  const optionByName = useMemo(() => {
    const m = new Map<string, GpuOption>();
    for (const opt of catalog?.gpus ?? []) m.set(opt.name, opt);
    return m;
  }, [catalog]);

  const hasUnknownEntry =
    incoming.length > 0 &&
    incoming.some((e) => !optionByName.has(e.name));

  // Default to advanced free-form when the catalog can't be reached
  // *and* there's already a value the picker would otherwise drop;
  // when the catalog is loaded we trust the parse_acceleratorList
  // round-trip (custom rows render inline so advanced isn't forced).
  const [advanced, setAdvanced] = useState(
    () => !catalog && value.trim() !== "" && incoming.length === 0,
  );

  if (!catalog || advanced) {
    return (
      <div className="resource-picker resource-picker--advanced">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="H100:1, A6000:1,RTXA6000:1, …"
        />
        <div className="resource-picker__advanced-toggle">
          {isLoading ? (
            <span className="muted">loading catalog…</span>
          ) : catalog && catalog.gpus.length > 0 ? (
            <button
              type="button"
              className="link-button"
              onClick={() => setAdvanced(false)}
            >
              ← use picker
            </button>
          ) : (
            <span className="muted">
              catalog unavailable — free-form input only
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <GpuPickerPanel
      incoming={incoming}
      hasUnknownEntry={hasUnknownEntry}
      optionByName={optionByName}
      onChange={onChange}
      onAdvanced={() => setAdvanced(true)}
      value={value}
    />
  );
}

interface GpuPickerPanelProps {
  incoming: Array<{ name: string; count: number }>;
  hasUnknownEntry: boolean;
  optionByName: Map<string, GpuOption>;
  onChange: (next: string) => void;
  onAdvanced: () => void;
  value: string;
}

function GpuPickerPanel({
  incoming,
  hasUnknownEntry,
  optionByName,
  onChange,
  onAdvanced,
  value,
}: GpuPickerPanelProps) {
  // Local-only UI state. The "source of truth" for what's saved is
  // the parent's ``value``; we re-derive ``selected`` from that on
  // mount and whenever ``value`` changes shape from outside (a
  // sibling that issues PATCH /pools, the discard button, …). We
  // keep an explicit ref to the last serialized value to avoid the
  // feedback loop where our own onChange re-rerenders the parent and
  // we then clobber the just-edited local state.
  const [selected, setSelected] = useState<
    Array<{ name: string; count: number }>
  >(() => incoming);
  const [filter, setFilter] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [defaultCount, setDefaultCount] = useState<number>(() =>
    incoming.length > 0 ? incoming[0].count : 1,
  );
  const lastEmitted = useRef<string>(value);

  // Reconcile local state with an externally-changed value (e.g. the
  // pool was reloaded from the server). Compare the serialized form
  // so an in-flight onChange round-trip ("we emitted X, parent gives
  // us X back") doesn't reset the panel.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    setSelected(incoming);
  }, [value, incoming]);

  // Build the rendered row list. Catalog GPUs sorted alphabetically
  // with custom (catalog-missing but currently-selected) rows
  // prepended so the operator's existing value stays visible.
  type Row = {
    name: string;
    option: GpuOption | undefined;
    isCustom: boolean;
  };
  const rows = useMemo<Row[]>(() => {
    const seen = new Set<string>();
    const out: Row[] = [];
    if (hasUnknownEntry) {
      for (const entry of selected) {
        if (!optionByName.has(entry.name) && !seen.has(entry.name)) {
          out.push({ name: entry.name, option: undefined, isCustom: true });
          seen.add(entry.name);
        }
      }
    }
    const sortedNames = [...optionByName.keys()].sort((a, b) =>
      a.localeCompare(b),
    );
    for (const n of sortedNames) {
      if (seen.has(n)) continue;
      out.push({ name: n, option: optionByName.get(n), isCustom: false });
    }
    return out;
  }, [optionByName, selected, hasUnknownEntry]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (q !== "") {
        const inName = r.name.toLowerCase().includes(q);
        const inCloud =
          r.option?.providers.some((p) =>
            humanCloudLabel(p.cloud).toLowerCase().includes(q),
          ) ?? false;
        if (!inName && !inCloud) return false;
      }
      if (enabledOnly) {
        // Selected rows always pass enabled-only so a row currently
        // in the value doesn't disappear from the panel on toggle —
        // confusing UX and impossible to recover from without
        // remembering the GPU's exact name.
        const isSelected = selected.some((e) => e.name === r.name);
        if (isSelected) return true;
        if (!r.option) return false;
        if (!r.option.providers.some((p) => p.enabled)) return false;
      }
      return true;
    });
  }, [rows, filter, enabledOnly, selected]);

  // Keep the row order stable across selection toggles. We used to
  // pin selected rows to the top, but that made every checkbox click
  // jump the row up or down under the cursor — confusing UX,
  // especially when ticking several similarly-named rows in a row
  // (RTX6000-ADA vs RTX-6000-ADA vs RTX6000ADA). ``rows`` is already
  // alphabetical with custom entries prepended, so reuse that order
  // verbatim and let ``is-selected`` styling carry the visual cue.
  const sortedFiltered = filtered;

  const emit = (next: Array<{ name: string; count: number }>) => {
    setSelected(next);
    const serialized = serializeAcceleratorList(next);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  const toggleRow = (row: Row) => {
    const isSelected = selected.some((e) => e.name === row.name);
    if (isSelected) {
      emit(selected.filter((e) => e.name !== row.name));
      return;
    }
    // Newly-selected row: use the panel's default count, snapped to
    // the closest count the catalog actually has rows for (avoids
    // committing ``A100:3`` when the catalog only has ``A100:1`` /
    // ``A100:8``, which would be a guaranteed provisioning failure).
    const counts = row.option?.counts ?? [];
    const snap = (n: number): number => {
      if (counts.length === 0) return n; // custom row, no constraint
      if (counts.includes(n)) return n;
      // pick the smallest catalog count >= requested, else the
      // largest available — the "smaller count is closer" heuristic
      // matches the optimiser's price preference.
      const larger = counts.find((c) => c >= n);
      return larger ?? counts[counts.length - 1];
    };
    emit([...selected, { name: row.name, count: snap(defaultCount) }]);
  };

  const updateRowCount = (name: string, count: number) => {
    if (!Number.isFinite(count) || count < 1) return;
    emit(
      selected.map((e) =>
        e.name === name ? { name: e.name, count } : e,
      ),
    );
  };

  const clearSelection = () => emit([]);

  // Union of every count any catalog GPU ships, capped at the values
  // operators actually pick (the long tail of e.g. ``128x``
  // PrimeIntellect rows would blow up the dropdown). 1/2/4/8 covers
  // ~all production pools we've seen in the audit.
  const commonCounts = useMemo(() => {
    const set = new Set<number>([1, 2, 4, 8]);
    for (const opt of optionByName.values()) {
      for (const c of opt.counts) {
        if (c <= 16) set.add(c);
      }
    }
    return [...set].sort((a, b) => a - b);
  }, [optionByName]);

  const summary =
    selected.length === 0
      ? "No GPUs selected."
      : `Selected: ${selected.length} ${
          selected.length === 1 ? "GPU" : "GPUs"
        } (${selected.map((e) => `${e.name}:${e.count}`).join(", ")})`;

  // Warn when every selected row has zero enabled providers — same
  // signal the previous single-select picker surfaced, just promoted
  // to a panel-level callout because each row already shows its own
  // chip strip.
  const noEnabledProvider =
    selected.length > 0 &&
    selected.every((e) => {
      const opt = optionByName.get(e.name);
      if (!opt) return false;
      return opt.providers.every((p) => !p.enabled);
    });

  return (
    <div className="resource-picker resource-picker__panel">
      <div className="resource-picker__panel-toolbar">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search GPU or provider…"
          className="resource-picker__panel-search"
        />
        <label className="resource-picker__panel-toggle">
          <input
            type="checkbox"
            checked={enabledOnly}
            onChange={(e) => setEnabledOnly(e.target.checked)}
          />
          enabled providers only
        </label>
        <label className="resource-picker__panel-default-count">
          default count
          <select
            value={defaultCount}
            onChange={(e) => setDefaultCount(Number(e.target.value))}
          >
            {commonCounts.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="resource-picker__panel-list" role="listbox">
        {sortedFiltered.length === 0 ? (
          <div className="resource-picker__panel-empty">
            No GPUs match the current filter.
          </div>
        ) : (
          sortedFiltered.map((row) => {
            const sel = selected.find((e) => e.name === row.name);
            const isSelected = sel != null;
            const counts = row.option?.counts ?? [];
            const rowWarn =
              row.option != null &&
              row.option.providers.length > 0 &&
              row.option.providers.every((p) => !p.enabled);
            return (
              <div
                key={row.name}
                className={
                  "resource-picker__panel-row" +
                  (isSelected ? " is-selected" : "") +
                  (rowWarn ? " is-warn" : "")
                }
                role="option"
                aria-selected={isSelected}
              >
                <label className="resource-picker__panel-rowmain">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleRow(row)}
                  />
                  <span className="resource-picker__panel-name">
                    {row.name}
                    {row.isCustom && (
                      <span className="resource-picker__panel-custom">
                        custom
                      </span>
                    )}
                  </span>
                </label>
                {isSelected && (
                  <span className="resource-picker__panel-count">
                    <span className="resource-picker__times">×</span>
                    {counts.length > 0 ? (
                      <select
                        value={sel.count}
                        onChange={(e) =>
                          updateRowCount(row.name, Number(e.target.value))
                        }
                      >
                        {counts.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                        {!counts.includes(sel.count) && (
                          // Existing value not in the catalog's
                          // counts list — keep it selectable so the
                          // operator can save without surprises.
                          <option value={sel.count}>
                            {sel.count} (custom)
                          </option>
                        )}
                      </select>
                    ) : (
                      <input
                        type="number"
                        min={1}
                        value={sel.count}
                        onChange={(e) =>
                          updateRowCount(row.name, Number(e.target.value))
                        }
                        className="resource-picker__panel-countinput"
                      />
                    )}
                  </span>
                )}
                {row.option ? (
                  <ProviderChips providers={row.option.providers} />
                ) : (
                  <span className="resource-picker__chip resource-picker__chip--none">
                    not in catalog
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
      <div className="resource-picker__panel-footer">
        <span className="resource-picker__panel-summary">{summary}</span>
        <div className="resource-picker__panel-actions">
          {selected.length > 0 && (
            <button
              type="button"
              className="link-button"
              onClick={clearSelection}
            >
              clear
            </button>
          )}
          <button
            type="button"
            className="link-button"
            onClick={onAdvanced}
          >
            Advanced — type CSV
          </button>
        </div>
      </div>
      {noEnabledProvider && (
        <div className="resource-picker__warn">
          None of the selected GPUs have an enabled provider in this
          pool. Enable a matching provider on the{" "}
          <strong>Providers</strong> tab or pick GPUs whose chips light
          up below.
        </div>
      )}
    </div>
  );
}

/**
 * Parse a CpuShape into a stable string key for option-matching.
 *
 * SkyPilot's selectors fold ``8`` and ``8+`` into the same row when
 * compared loosely so we keep both halves on the key (``8|16`` vs
 * ``8|`` for "memory unset").
 */
function cpuShapeKey(cpus: string, memoryGb: string | null): string {
  return `${cpus}|${memoryGb ?? ""}`;
}

interface CpuPickerProps {
  cpus: string;
  memoryGb: string;
  onChange: (next: { cpus: string; memoryGb: string }) => void;
  catalog: ResourceCatalog | undefined;
  isLoading: boolean;
}

/**
 * CPU shape picker — one combined select listing every shape the
 * catalog offers (exact RunPod cpu3*-N-M / cpu5*-N-M rows plus
 * open-range buckets ``2+`` … ``64+`` for non-fixed-ratio clouds).
 * Picking a
 * shape sets ``cpus`` and ``memory_gb`` together so the operator
 * can't end up with the "exact cpus + range memory" trap the
 * renderer warns about. Falls back to advanced free-form inputs the
 * same way the GPU picker does.
 */
export function CpuPicker({
  cpus,
  memoryGb,
  onChange,
  catalog,
  isLoading,
}: CpuPickerProps) {
  const shapes = catalog?.cpu_shapes ?? [];
  const byKey = useMemo(() => {
    const m = new Map<string, CpuShape>();
    for (const s of shapes) m.set(cpuShapeKey(s.cpus, s.memory_gb), s);
    return m;
  }, [shapes]);

  const currentKey = cpuShapeKey(
    cpus.trim(),
    memoryGb.trim() === "" ? null : memoryGb.trim(),
  );
  const currentShape = byKey.get(currentKey);

  const [advanced, setAdvanced] = useState(
    () =>
      // Open in advanced mode if the current cpus/memory doesn't
      // match any catalog row — preserves hand-tuned overrides
      // unchanged across re-mounts.
      (cpus.trim() !== "" || memoryGb.trim() !== "") && !currentShape,
  );

  if (!catalog || advanced) {
    return (
      <div className="resource-picker resource-picker--advanced">
        <div className="resource-picker__advanced-fields">
          <label>cpus</label>
          <input
            value={cpus}
            onChange={(e) => onChange({ cpus: e.target.value, memoryGb })}
            placeholder="2+, 8+, 16+, …"
          />
          <label>memory (GiB)</label>
          <input
            value={memoryGb}
            onChange={(e) => onChange({ cpus, memoryGb: e.target.value })}
            placeholder="(optional) 4+, 16+, 64+, …"
          />
        </div>
        <div className="resource-picker__advanced-toggle">
          {isLoading ? (
            <span className="muted">loading catalog…</span>
          ) : catalog && catalog.cpu_shapes.length > 0 ? (
            <button
              type="button"
              className="link-button"
              onClick={() => setAdvanced(false)}
            >
              ← use picker
            </button>
          ) : (
            <span className="muted">
              catalog unavailable — free-form input only
            </span>
          )}
        </div>
      </div>
    );
  }

  const handleShapeChange = (key: string) => {
    if (!key) {
      onChange({ cpus: "", memoryGb: "" });
      return;
    }
    const shape = byKey.get(key);
    if (!shape) return;
    onChange({
      cpus: shape.cpus,
      memoryGb: shape.memory_gb ?? "",
    });
  };

  const noEnabledProvider =
    currentShape != null &&
    currentShape.providers.every((p) => !p.enabled);

  return (
    <div className="resource-picker">
      <div className="resource-picker__row">
        <select
          value={currentKey}
          onChange={(e) => handleShapeChange(e.target.value)}
          className="resource-picker__select resource-picker__select--cpu"
        >
          <option value="|">— pick a CPU shape —</option>
          {shapes.map((s) => {
            const key = cpuShapeKey(s.cpus, s.memory_gb);
            const label = s.memory_gb
              ? `${s.cpus} vCPU · ${s.memory_gb} GiB RAM`
              : `${s.cpus} vCPU · (any RAM)`;
            const enabledCount = s.providers.filter((p) => p.enabled)
              .length;
            return (
              <option key={key} value={key}>
                {label}
                {enabledCount === 0 ? "  ⚠ no enabled provider" : ""}
              </option>
            );
          })}
        </select>
        {currentShape && (
          <ProviderChips providers={currentShape.providers} />
        )}
      </div>
      {currentShape?.note && (
        <div className="resource-picker__note">{currentShape.note}</div>
      )}
      {noEnabledProvider && (
        <div className="resource-picker__warn">
          No enabled provider can serve this shape. Enable a matching
          provider on the <strong>Providers</strong> tab or pick a
          different shape.
        </div>
      )}
      <div className="resource-picker__advanced-toggle">
        <button
          type="button"
          className="link-button"
          onClick={() => setAdvanced(true)}
        >
          Advanced — type a custom selector
        </button>
      </div>
    </div>
  );
}
